import { Injectable, inject } from '@angular/core';
import {
  Action,
  createSelector,
  Selector,
  State,
  StateContext,
  Store,
} from '@ngxs/store';
import { AuthState } from '@wilson/auth/core';
import {
  COMPACT_ROW_HEIGHT,
  COMPACT_SHIFT_HEIGHT,
  MILLISECONDS_IN_A_DAY,
  ZOOM_LEVEL,
} from '@wilson/interfaces';
import {
  SideNavSettingModel,
  SideNavSettingsState,
} from '@wilson/side-nav-settings/state';
import { ShiftTimelineSettingsModel } from '../interfaces';
import {
  ResetRowHeight,
  ResetShiftTimelineWidth,
  SetAreActivitiesVisible,
  SetAreStaysVisible,
  SetCellViewUnassignedRegionHeightPx,
  SetDefaultRowHeight,
  SetDefaultShiftHeight,
  SetRegularViewUnassignedRegionHeightPx,
  SetZoomLevel,
  UpdateWindowWidthHeightToWindowInnerWidthHeight,
} from './shift-timeline-settings.actions';

export const timelineDaysByZoomLevel = new Map([
  [ZOOM_LEVEL.MONTH, 31],
  [ZOOM_LEVEL.HALF_MONTH, 14],
  [ZOOM_LEVEL.TEN_DAYS, 10],
  [ZOOM_LEVEL.DEFAULT, 7],
  [ZOOM_LEVEL.HALF_WEEK, 3],
  [ZOOM_LEVEL.DAY, 1],
]);

const PIXEL_EXCEPT_TIMELINE_WIDTH_WHEN_SIDENAV_OPEN = 322; // This ADJUSTMENT_PIXEL is calculated from Sidebar(px) + Page Frame Padding(px) + FIlter section(px)
const PIXEL_EXCEPT_TIMELINE_WIDTH_WHEN_SIDENAV_CLOSE = 130; // This ADJUSTMENT_PIXEL is calculated from Sidebar(px) + Page Frame Padding(px) + FIlter section(px)

const hoursPerZoomLevel = new Map([
  [ZOOM_LEVEL.MONTH, []], // On 31 Days View we are not showing any hours so make this array empty
  [ZOOM_LEVEL.HALF_MONTH, []], // On 14 Days View we are not showing any hours so make this array empty
  [ZOOM_LEVEL.TEN_DAYS, []], // On 10 Days View we are not showing any hours so make this array empty
  [ZOOM_LEVEL.DEFAULT, ['00:00', '08:00', '16:00', '24:00']],
  [
    ZOOM_LEVEL.HALF_WEEK,
    ['00:00', '04:00', '08:00', '12:00', '16:00', '20:00', '24:00'],
  ],
  [
    ZOOM_LEVEL.DAY,
    [
      '00:00',
      '01:00',
      '02:00',
      '03:00',
      '04:00',
      '05:00',
      '06:00',
      '07:00',
      '08:00',
      '09:00',
      '10:00',
      '11:00',
      '12:00',
      '13:00',
      '14:00',
      '15:00',
      '16:00',
      '17:00',
      '18:00',
      '19:00',
      '20:00',
      '21:00',
      '22:00',
      '23:00',
      '24:00',
    ],
  ],
]);
const defaultResourcesColumnWidthPx = 288;

const defaults: ShiftTimelineSettingsModel = {
  windowWidth: window.innerWidth,
  windowHeight: window.innerHeight,
  zoomLevel: ZOOM_LEVEL.DEFAULT,
  hoursRange: hoursPerZoomLevel.get(ZOOM_LEVEL.DEFAULT) as string[],
  resourcesColumnWidthPx: defaultResourcesColumnWidthPx,
  defaultRowHeight: COMPACT_ROW_HEIGHT,
  defaultShiftHeight: COMPACT_SHIFT_HEIGHT,
  defaultUnassignedSectionHeight: 150,
  areActivitiesVisible: false,
  areStaysVisible: false,
  userRegularViewUnassignedRegionHeightPx: null,
  userCellViewUnassignedRegionHeightPx: null,
};

@State({
  name: 'shiftTimelineSettings',
  defaults,
})
@Injectable()
export class ShiftTimelineSettingsState {
  private readonly store = inject(Store);

  @Selector()
  static defaultUnassignedSectionHeight(
    state: ShiftTimelineSettingsModel,
  ): number {
    return state.defaultUnassignedSectionHeight;
  }

  @Selector()
  static resourcesColumnWidthPx(state: ShiftTimelineSettingsModel): number {
    return state.resourcesColumnWidthPx;
  }

  @Selector()
  static userRegularViewUnassignedRegionHeightPx(
    state: ShiftTimelineSettingsModel,
  ): number | null {
    return state.userRegularViewUnassignedRegionHeightPx;
  }

  @Selector()
  static userCellViewUnassignedRegionHeightPx(
    state: ShiftTimelineSettingsModel,
  ): number | null {
    return state.userCellViewUnassignedRegionHeightPx;
  }

  @Selector()
  static settings(
    state: ShiftTimelineSettingsModel,
  ): ShiftTimelineSettingsModel {
    return state;
  }

  @Selector()
  static hoursRange(state: ShiftTimelineSettingsModel): string[] {
    return state.hoursRange;
  }

  @Selector()
  static numberOfDaysFromZoomLevel(state: ShiftTimelineSettingsModel): number {
    return timelineDaysByZoomLevel.get(state.zoomLevel) as number;
  }

  @Selector()
  static zoomLevel(state: ShiftTimelineSettingsModel): ZOOM_LEVEL {
    return state.zoomLevel;
  }

  @Selector()
  static defaultRowHeight(state: ShiftTimelineSettingsModel): number {
    return state.defaultRowHeight;
  }

  @Selector()
  static defaultShiftHeight(state: ShiftTimelineSettingsModel): number {
    return state.defaultShiftHeight;
  }

  @Selector()
  static areActivitiesVisible(state: ShiftTimelineSettingsModel): boolean {
    return state.areActivitiesVisible;
  }

  @Selector()
  static areStaysVisible(state: ShiftTimelineSettingsModel): boolean {
    return state.areStaysVisible;
  }

  @Selector()
  static windowWidth(state: ShiftTimelineSettingsModel): number {
    return state.windowWidth;
  }

  @Selector()
  static windowHeight(state: ShiftTimelineSettingsModel): number {
    return state.windowHeight;
  }

  static pixelWidthPerDay(): (
    userId: string,
    zoomLevel: ZOOM_LEVEL,
    resourcesColumnWidthPx: number,
    sideNavSettings: SideNavSettingModel,
    windowWidth: number,
  ) => number {
    return createSelector(
      [
        AuthState.userId,
        ShiftTimelineSettingsState.zoomLevel,
        ShiftTimelineSettingsState.resourcesColumnWidthPx,
        SideNavSettingsState,
        ShiftTimelineSettingsState.windowWidth,
      ],
      (
        userId: string,
        zoomLevel: ZOOM_LEVEL,
        resourcesColumnWidthPx: number,
        sideNavSettings: SideNavSettingModel,
        windowWidth: number,
      ) => {
        const isSidenavCollapsed = userId
          ? !!sideNavSettings.sideNavSettings[userId]?.isCollapsed
          : false;

        const pixelExceptTimelineWidth = isSidenavCollapsed
          ? PIXEL_EXCEPT_TIMELINE_WIDTH_WHEN_SIDENAV_CLOSE
          : PIXEL_EXCEPT_TIMELINE_WIDTH_WHEN_SIDENAV_OPEN;

        const pixelWidthPerDay =
          (windowWidth - pixelExceptTimelineWidth - resourcesColumnWidthPx) /
            (timelineDaysByZoomLevel.get(zoomLevel) as number) || 0;

        // This value must be rounded to prevent decimal numbers in frame length calculation,
        // which the browsers will automatically round up and causes issues for us
        return Math.round(pixelWidthPerDay);
      },
    );
  }

  static pixelPerMs(): (pixelWidthPerDay: number) => number {
    return createSelector(
      [ShiftTimelineSettingsState.pixelWidthPerDay()],
      (pixelWidthPerDay) => pixelWidthPerDay / MILLISECONDS_IN_A_DAY,
    );
  }

  @Action(SetZoomLevel)
  SetZoomLevel(
    { patchState }: StateContext<ShiftTimelineSettingsModel>,
    { zoomLevel }: SetZoomLevel,
  ): void {
    patchState({
      zoomLevel,
      hoursRange: hoursPerZoomLevel.get(zoomLevel) as string[],
    });
  }

  @Action(SetAreStaysVisible)
  setAreStaysVisible(
    { patchState }: StateContext<ShiftTimelineSettingsModel>,
    { isVisible }: SetAreStaysVisible,
  ): void {
    patchState({
      areStaysVisible: isVisible,
    });
  }

  @Action(SetAreActivitiesVisible)
  setAreActivitiesVisible(
    { patchState }: StateContext<ShiftTimelineSettingsModel>,
    { isVisible }: SetAreActivitiesVisible,
  ): void {
    patchState({
      areActivitiesVisible: isVisible,
    });
  }

  @Action(SetDefaultRowHeight)
  setDefaultRowHeight(
    { patchState }: StateContext<ShiftTimelineSettingsModel>,
    { height }: SetDefaultRowHeight,
  ): void {
    patchState({
      defaultRowHeight: height,
    });
  }

  @Action(SetDefaultShiftHeight)
  setDefaultShiftHeight(
    { patchState }: StateContext<ShiftTimelineSettingsModel>,
    { height }: SetDefaultShiftHeight,
  ): void {
    patchState({
      defaultShiftHeight: height,
    });
  }

  @Action(SetRegularViewUnassignedRegionHeightPx)
  setRegularViewUnassignedRegionHeightPx(
    { patchState }: StateContext<ShiftTimelineSettingsModel>,
    { heightPx }: SetRegularViewUnassignedRegionHeightPx,
  ): void {
    patchState({
      userRegularViewUnassignedRegionHeightPx: heightPx,
    });
  }

  @Action(SetCellViewUnassignedRegionHeightPx)
  setCellViewUnassignedRegionHeightPx(
    { patchState }: StateContext<ShiftTimelineSettingsModel>,
    { heightPx }: SetCellViewUnassignedRegionHeightPx,
  ): void {
    patchState({
      userCellViewUnassignedRegionHeightPx: heightPx,
    });
  }

  @Action(ResetShiftTimelineWidth)
  resetShiftTimelineWidth({
    patchState,
  }: StateContext<ShiftTimelineSettingsModel>): void {
    patchState({
      resourcesColumnWidthPx: defaults.resourcesColumnWidthPx,
    });
  }

  @Action(ResetRowHeight)
  resetRowHeight({
    patchState,
  }: StateContext<ShiftTimelineSettingsModel>): void {
    patchState({
      defaultRowHeight: defaults.defaultRowHeight,
    });
  }

  @Action(UpdateWindowWidthHeightToWindowInnerWidthHeight)
  updateWindowWidthHeightToWindowInnerWidthHeight({
    patchState,
  }: StateContext<ShiftTimelineSettingsModel>): void {
    patchState({
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
    });
  }
}
